// Schematic sheets: a free-form block diagram drawn in paper-mm space on a sheet
// (no model viewport). Boxes, free lines, box-to-box connections (rendered like
// part connections — parallel strands, discipline colour, badge, routing, and
// bridges where strands of the same discipline cross) and imported images.

import type {
  SchematicBox,
  SchematicConn,
  SchematicDim,
  SchematicLine,
  Sheet,
  SheetViewport,
  Vec2,
} from '../shared/types';
import { paperDimFigure } from './paperDim';
import {
  paperToScreen,
  type PaperView,
  rectHandles,
} from './sheets';
import {
  CONNECTION_COLORS,
  drawArrow,
  drawTag,
  nearestRectPoint,
  separateTags,
  TEXTBOX_CORNER_DEFAULT,
} from './render';
import {
  dashArray,
  segCross,
  strokeWithHumps,
} from './lineHops';
import {
  BAND_MITRE,
  offsetPolyline,
  pointAtFraction,
} from './geometry';
import { drawGrip } from './grip';
import { hexToRgba } from './glass';
import {
  schConnRoute,
  type SchemSel,
} from './sheetObjects';

export type { SchemSel } from './sheetObjects';
export {
  schBoxAnchor,
  schBoxCenter,
  schConnRoute,
} from './sheetObjects';

// Loaded <img> elements keyed by data URL, so paint doesn't reload each frame.
const imgCache = new Map<string, HTMLImageElement>();
function getImage(dataUrl: string, onLoad: () => void): HTMLImageElement | null {
  const cached = imgCache.get(dataUrl);
  if (cached) return cached.complete && cached.naturalWidth > 0 ? cached : null;
  const img = new Image();
  img.onload = onLoad;
  img.src = dataUrl;
  imgCache.set(dataUrl, img);
  return null;
}

/** Ensure every image on a schematic sheet is decoded before a one-shot render
 *  (e.g. printing), which can't wait for lazy onload callbacks. */
export async function preloadSchematicImages(sh: Sheet): Promise<void> {
  const c = sh.schematic;
  if (!c) return;
  await Promise.all(c.images.map((im) => new Promise<void>((resolve) => {
    let img = imgCache.get(im.dataUrl);
    if (!img) { img = new Image(); img.src = im.dataUrl; imgCache.set(im.dataUrl, img); }
    if (img.complete) resolve(); else { img.onload = () => resolve(); img.onerror = () => resolve(); }
  })));
}

/** Default stroke width (paper mm) of a schematic connection, matching the
 *  1.5 px it was drawn at before connections gained a thickness. */
export const SCH_CONN_THICKNESS = 0.6;


/** Millimetres per typographic point (1 pt = 1/72 inch). */
export const MM_PER_PT = 25.4 / 72;
/** Default box text size in points (word-processor style). */
export const DEFAULT_BOX_FONT_PT = 10;
/** The old 6 mm default expressed in points, so legacy `fontScale` boxes keep
 *  their exact size when read through the new points model. */
const LEGACY_MM6_PT = 6 / MM_PER_PT;

/** A box's text size in points: the stored `fontPt`, else the legacy `fontScale`
 *  converted from its old 6 mm base, else the 10 pt default. */
export function boxFontPt(b: SchematicBox): number {
  return b.fontPt ?? (b.fontScale != null ? b.fontScale * LEGACY_MM6_PT : DEFAULT_BOX_FONT_PT);
}
/** A box's text height in paper mm (points → mm). */
export function boxFontMm(b: SchematicBox): number { return boxFontPt(b) * MM_PER_PT; }

/** Connection-badge text height (px) at a given paper scale: a fixed 10 pt on the
 *  page (so it prints at a consistent size and matches the default box text),
 *  scaling with zoom like every other paper-space element. Shared by the draw and
 *  the badge hit-test so a badge is grabbed exactly where it is drawn. */
export function schBadgePx(paperScale: number): number { return DEFAULT_BOX_FONT_PT * MM_PER_PT * paperScale; }
/** Where a connection's badge sits along its route, as a fraction of the length. */
export function schBadgeT(c: SchematicConn): number { return Math.max(0, Math.min(1, c.badgeT ?? 0.5)); }

function inRect(p: Vec2, x: number, y: number, w: number, h: number): boolean {
  return p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h;
}
function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  let t = l2 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Topmost schematic object at paper point `p` (boxes → connections → lines →
 *  images, matching the paint order). `tol` is the pick tolerance in paper mm. */
export function schHitObject(sh: Sheet, p: Vec2, tol: number): SchemSel | null {
  const c = sh.schematic;
  if (!c) return null;
  // Dimensions are drawn last (on top), so they are picked first: on the line.
  for (let i = (c.dims?.length ?? 0) - 1; i >= 0; i--) {
    const d = c.dims![i];
    const f = paperDimFigure(d, sh.viewports);
    if (f && distToSeg(p, f.geom.a, f.geom.b) <= Math.max(tol, 1.5)) return { kind: 'dim', id: d.id };
  }
  for (let i = c.boxes.length - 1; i >= 0; i--) { const b = c.boxes[i]; if (inRect(p, b.x, b.y, b.w, b.h)) return { kind: 'box', id: b.id }; }
  for (let i = c.conns.length - 1; i >= 0; i--) {
    const route = schConnRoute(sh, c.conns[i]);
    if (route) for (let s = 0; s < route.length - 1; s++) if (distToSeg(p, route[s], route[s + 1]) <= tol) return { kind: 'conn', id: c.conns[i].id };
  }
  for (let i = c.lines.length - 1; i >= 0; i--) {
    const ln = c.lines[i], pts = ln.points;
    for (let s = 0; s < pts.length - 1; s++) if (distToSeg(p, pts[s], pts[s + 1]) <= tol) return { kind: 'line', id: ln.id };
    if (ln.closed && pts.length > 2 && distToSeg(p, pts[pts.length - 1], pts[0]) <= tol) return { kind: 'line', id: ln.id };
    if (ln.closed && ln.fillColor && inRing(p, pts)) return { kind: 'line', id: ln.id };
  }
  for (let i = c.images.length - 1; i >= 0; i--) { const im = c.images[i]; if (inRect(p, im.x, im.y, im.w, im.h)) return { kind: 'image', id: im.id }; }
  return null;
}

/** Even-odd point-in-polygon for a paper ring. */
function inRing(p: Vec2, pts: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** The rectangle (paper mm) of a box or image, or null for other kinds. */
export function schSelRect(sh: Sheet, sel: SchemSel): { x: number; y: number; w: number; h: number } | null {
  const c = sh.schematic; if (!c) return null;
  if (sel.kind === 'box') { const b = c.boxes.find((x) => x.id === sel.id); return b ? { x: b.x, y: b.y, w: b.w, h: b.h } : null; }
  if (sel.kind === 'image') { const im = c.images.find((x) => x.id === sel.id); return im ? { x: im.x, y: im.y, w: im.w, h: im.h } : null; }
  return null;
}

/** Local schedule numbers (E1/P1/…) per discipline for a sheet's connections. */
export function schConnNumbers(conns: SchematicConn[]): Map<string, string> {
  const prefix: Record<string, string> = { electrical: 'E', plumbing: 'P', mechanical: 'M', drainage: 'D' };
  const counts: Record<string, number> = {};
  const out = new Map<string, string>();
  for (const c of conns) {
    const t = c.type ?? 'electrical';
    counts[t] = (counts[t] ?? 0) + 1;
    out.set(c.id, `${prefix[t] ?? '?'}${counts[t]}`);
  }
  return out;
}

/** A wrapped line plus the original-text span [start, end) it was drawn from, so a
 *  caret index can be mapped back to a line and an x offset. */
interface WrapLine { text: string; start: number; end: number }

/** Wrap `text` to `maxW` px using the current ctx font, tracking each line's
 *  original-text span. Honours explicit newlines and word-wraps within each. */
function wrapTextIndexed(ctx: CanvasRenderingContext2D, text: string, maxW: number): WrapLine[] {
  const out: WrapLine[] = [];
  let pos = 0;
  for (const raw of text.split('\n')) {
    const words: Array<{ w: string; at: number }> = [];
    const re = /\S+/g; let m: RegExpExecArray | null;
    while ((m = re.exec(raw)) !== null) words.push({ w: m[0], at: pos + m.index });
    if (words.length === 0) {
      out.push({ text: '', start: pos, end: pos });
    } else {
      let cur: WrapLine | null = null;
      for (const tok of words) {
        const joined: string = cur ? cur.text + ' ' + tok.w : tok.w;
        if (cur && ctx.measureText(joined).width > maxW) {
          out.push(cur);
          cur = { text: tok.w, start: tok.at, end: tok.at + tok.w.length };
        } else {
          cur = { text: joined, start: cur ? cur.start : tok.at, end: tok.at + tok.w.length };
        }
      }
      if (cur) out.push(cur);
    }
    pos += raw.length + 1; // consume the '\n'
  }
  return out;
}

/** Index of the wrapped line a caret sits on: the last line starting at or before it. */
function caretLine(lines: WrapLine[], caret: number): number {
  let idx = 0;
  for (let i = 0; i < lines.length; i++) if (lines[i].start <= caret) idx = i;
  return idx;
}

/** State passed to drawBox for the box currently being edited: caret index and
 *  whether the (blinking) caret is in its ON phase. */
export interface BoxEditState { caret: number; on: boolean }

function drawBox(ctx: CanvasRenderingContext2D, pv: PaperView, b: SchematicBox, ink: string, edit: BoxEditState | null = null): void {
  const a = paperToScreen(pv, b.x, b.y);
  const w = b.w * pv.scale, h = b.h * pv.scale;
  // Corner rounding is a paper-mm property, clamped to half the shorter side.
  const r = Math.max(0, Math.min((b.cornerRadius ?? TEXTBOX_CORNER_DEFAULT) * pv.scale, w / 2, h / 2));
  ctx.save();
  // Leader arrow first, so the box's own fill covers where it meets the border.
  if (b.leader) {
    const target = paperToScreen(pv, b.leader.x, b.leader.y);
    const start = nearestRectPoint(a.x, a.y, w, h, target);
    ctx.strokeStyle = hexToRgba(b.borderColor || ink, b.borderOpacity ?? 1);
    ctx.fillStyle = hexToRgba(b.borderColor || ink, b.borderOpacity ?? 1);
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    drawArrow(ctx, target, start);
  }
  ctx.beginPath();
  if (b.shape === 'ellipse') ctx.ellipse(a.x + w / 2, a.y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  else if (ctx.roundRect) ctx.roundRect(a.x, a.y, w, h, r); else ctx.rect(a.x, a.y, w, h);
  if (b.fillColor) { ctx.fillStyle = hexToRgba(b.fillColor, b.fillOpacity ?? 1); ctx.fill(); }
  ctx.strokeStyle = hexToRgba(b.borderColor || ink, b.borderOpacity ?? 1);
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // Text, wrapped and centred. Its size is the box's OWN text height in paper mm
  // — never derived from the box height, so resizing the box only re-wraps the
  // text — and it is not clipped, so text that no longer fits overflows. Drawn
  // live even while the box is being edited: the overlay editor hides its own
  // glyphs (caret only), so this canvas text IS the real-time WYSIWYG preview.
  // Drawn even for an empty box while editing, so its caret still shows.
  if (b.text || edit) {
    const mm = boxFontMm(b);
    const fs = Math.max(1, mm * pv.scale);
    ctx.font = `${fs}px system-ui, sans-serif`;
    ctx.fillStyle = hexToRgba(b.textColor || ink, b.textOpacity ?? 1);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const pad = 4;
    const lines = wrapTextIndexed(ctx, b.text, Math.max(4, w - 2 * pad));
    const lh = Math.max(10, fs * 1.2);
    const cLine = edit ? caretLine(lines, edit.caret) : -1;
    let caretAt: { x: number; y: number } | null = null;
    let ty = a.y + h / 2 - ((lines.length - 1) * lh) / 2;
    for (let i = 0; i < lines.length; i++) {
      const ln = lines[i];
      if (ln.text) ctx.fillText(ln.text, a.x + w / 2, ty);
      if (i === cLine) {
        const col = Math.max(ln.start, Math.min(edit!.caret, ln.end));
        const lineW = ctx.measureText(ln.text).width;
        const before = ctx.measureText(b.text.slice(ln.start, col)).width;
        caretAt = { x: a.x + w / 2 - lineW / 2 + before, y: ty };
      }
      ty += lh;
    }
    // The blinking caret — drawn on the canvas (the DOM editor's own caret is
    // hidden), so it lands exactly on the WYSIWYG text at its true size.
    if (edit?.on && caretAt) {
      ctx.strokeStyle = b.textColor || ink;
      ctx.lineWidth = Math.max(1, fs * 0.08);
      ctx.beginPath();
      ctx.moveTo(caretAt.x, caretAt.y - fs * 0.55);
      ctx.lineTo(caretAt.x, caretAt.y + fs * 0.55);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawLine(ctx: CanvasRenderingContext2D, pv: PaperView, ln: SchematicLine): void {
  if (ln.points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = ln.color;
  ctx.lineWidth = Math.max(0.5, ln.thickness * pv.scale);
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  ctx.setLineDash(dashArray(ln.style, Math.max(1, ctx.lineWidth)));
  ctx.beginPath();
  ln.points.forEach((p, i) => { const s = paperToScreen(pv, p.x, p.y); i === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y); });
  if (ln.closed) ctx.closePath();
  if (ln.closed && ln.fillColor) { ctx.fillStyle = ln.fillColor; ctx.fill(); }
  ctx.stroke();
  ctx.restore();
}

function drawConns(ctx: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, selIds: Set<string>): void {
  const conns = sh.schematic?.conns ?? [];
  if (!conns.length) return;
  const SPACING = 5;
  const badgePx = Math.max(6, schBadgePx(pv.scale)); // floor so a zoomed-out badge stays legible
  const numbers = schConnNumbers(conns);
  // Build screen-space routes.
  const routes = conns
    .map((c) => { const r = schConnRoute(sh, c); return r ? { c, screen: r.map((p) => paperToScreen(pv, p.x, p.y)) } : null; })
    .filter((r): r is { c: SchematicConn; screen: Vec2[] } => r !== null);

  // Lane-offset straight bundles that share the same box pair.
  const GAP = SPACING * 1.5;
  const baseOff = new Map<string, number>();
  const pairs = new Map<string, { c: SchematicConn; screen: Vec2[] }[]>();
  for (const r of routes) {
    if (r.screen.length !== 2) continue;
    const key = [r.c.from, r.c.to].sort().join('|');
    (pairs.get(key) ?? pairs.set(key, []).get(key)!).push(r);
  }
  for (const group of pairs.values()) {
    let cursor = -((group.length - 1) * GAP) / 2;
    for (const r of group) { baseOff.set(r.c.id, cursor); cursor += GAP; }
  }

  const drawnSegs: Array<{ type: string; a: Vec2; b: Vec2 }> = [];
  const tags: Array<{ x: number; y: number; w: number; text: string; color: string }> = [];
  ctx.save();
  for (const { c, screen } of routes) {
    const type = c.type ?? 'electrical';
    const sel = selIds.has(c.id);
    const color = sel ? '#ffd166' : (c.color || CONNECTION_COLORS[type]);
    const n = Math.max(1, c.lines);
    const base = baseOff.get(c.id) ?? 0;
    ctx.strokeStyle = color;
    // Thickness and dash are paper mm, the same measure a schematic line uses, so
    // a connection and a line of the same settings print identically.
    ctx.lineWidth = Math.max(0.5, (c.thickness ?? SCH_CONN_THICKNESS) * pv.scale);
    ctx.setLineDash(dashArray(c.style ?? 'dashed', pv.scale));
    for (let i = 0; i < n; i++) {
      const off = (i - (n - 1) / 2) * SPACING + base;
      const strand = offsetPolyline(screen, off, false, BAND_MITRE);
      for (let s = 0; s < strand.length - 1; s++) {
        const a = strand[s], b = strand[s + 1];
        const hops: Vec2[] = [];
        for (const seg of drawnSegs) { if (seg.type !== type) continue; const x = segCross(a, b, seg.a, seg.b); if (x) hops.push(x); }
        strokeWithHumps(ctx, a, b, hops, 6);
      }
    }
    // Register this route's centre strand for later routes to hop over.
    for (let s = 0; s < screen.length - 1; s++) drawnSegs.push({ type, a: screen[s], b: screen[s + 1] });
    // Badge at its stored position along the route (default mid-route), 10 pt
    // paper: its number, then its label.
    const at = pointAtFraction(screen, schBadgeT(c));
    const num = [numbers.get(c.id), c.label?.trim()].filter(Boolean).join(' ');
    if (num) { ctx.font = `bold ${badgePx}px sans-serif`; tags.push({ x: at.x, y: at.y, w: ctx.measureText(num).width + badgePx * (10 / 13), text: num, color }); }
  }
  ctx.setLineDash([]);
  separateTags(tags, badgePx * (19 / 13));
  for (const t of tags) drawTag(ctx, t.x, t.y, t.text, t.color, badgePx);
  ctx.restore();
}

/** Draw the whole schematic content of a sheet (images → lines → connections →
 *  boxes → selection). `ink` is the sheet ink colour; `onImageLoad` re-paints
 *  once a lazily-loaded image is ready. */
export function renderSchematic(ctx: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, ink: string, sel: SchemSel[], onImageLoad: () => void, editing: { boxId: string; caret: number; on: boolean } | null = null): void {
  const c = sh.schematic;
  if (!c) return;
  for (const im of c.images) {
    const a = paperToScreen(pv, im.x, im.y);
    const w = im.w * pv.scale, h = im.h * pv.scale;
    const img = getImage(im.dataUrl, onImageLoad);
    if (img) ctx.drawImage(img, a.x, a.y, w, h);
    else { ctx.save(); ctx.fillStyle = 'rgba(120,130,140,0.15)'; ctx.fillRect(a.x, a.y, w, h); ctx.restore(); }
  }
  for (const ln of c.lines) drawLine(ctx, pv, ln);
  drawConns(ctx, pv, sh, new Set(sel.filter((s) => s.kind === 'conn').map((s) => s.id)));
  for (const b of c.boxes) drawBox(ctx, pv, b, ink, editing && editing.boxId === b.id ? { caret: editing.caret, on: editing.on } : null);
  const selDims = new Set(sel.filter((s) => s.kind === 'dim').map((s) => s.id));
  for (const d of c.dims ?? []) drawPaperDim(ctx, pv, d, sh.viewports, ink, selDims.has(d.id));
  // Selection outline + handles: every selected object is outlined; the grab
  // handles show only for a lone selection (a multi-selection can't be resized).
  const withHandles = sel.length === 1;
  for (const s of sel) drawSelection(ctx, pv, sh, s, withHandles);
}

/** How a paper dimension writes its figure — set by the app (the drawing's style). */
let dimFormat: (mm: number) => string = (mm) => `${Math.round(mm)} mm`;
export function setPaperDimFormat(fn: (mm: number) => string): void { dimFormat = fn; }
/** Paper text height of a dimension figure (mm), as a drafting standard 2.5. */
const DIM_TEXT_MM = 2.5;

/** One paper dimension: extension lines, the dimension line with an arrow at each
 *  end, and the figure centred above it, turned to read left-to-right. `preview`
 *  draws the one being placed. */
export function drawPaperDim(ctx: CanvasRenderingContext2D, pv: PaperView, d: SchematicDim, viewports: readonly SheetViewport[], ink: string, selected = false): void {
  const f = paperDimFigure(d, viewports);
  if (!f) return;
  const S = (q: Vec2): Vec2 => paperToScreen(pv, q.x, q.y);
  const a = S(d.a), b = S(d.b), ga = S(f.geom.a), gb = S(f.geom.b);
  ctx.save();
  const col = selected ? '#4aa3ff' : d.color || ink;
  ctx.strokeStyle = col; ctx.fillStyle = col;
  ctx.lineWidth = selected ? 1.5 : 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y); ctx.lineTo(ga.x, ga.y);
  ctx.moveTo(b.x, b.y); ctx.lineTo(gb.x, gb.y);
  ctx.moveTo(ga.x, ga.y); ctx.lineTo(gb.x, gb.y);
  ctx.stroke();
  drawArrow(ctx, ga, gb);
  drawArrow(ctx, gb, ga);
  const fs = Math.max(8, DIM_TEXT_MM * pv.scale);
  let ang = Math.atan2(gb.y - ga.y, gb.x - ga.x);
  if (ang > Math.PI / 2) ang -= Math.PI; else if (ang < -Math.PI / 2) ang += Math.PI;
  ctx.translate((ga.x + gb.x) / 2, (ga.y + gb.y) / 2);
  ctx.rotate(ang);
  ctx.font = `${fs}px system-ui, sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
  ctx.fillText(dimFormat(f.mm), 0, -fs * 0.2);
  ctx.restore();
}



function drawSelection(ctx: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, sel: SchemSel, withHandles: boolean): void {
  const c = sh.schematic!;
  ctx.save();
  ctx.strokeStyle = '#4aa3ff'; ctx.lineWidth = 1.5; ctx.setLineDash([]);
  // The same grip as everywhere else (grip.ts).
  const handle = (x: number, y: number): void => drawGrip(ctx, { x, y });
  const rect = (x: number, y: number, w: number, h: number): void => {
    const a = paperToScreen(pv, x, y);
    ctx.strokeRect(a.x, a.y, w * pv.scale, h * pv.scale);
    if (withHandles) for (const hd of rectHandles({ x: a.x, y: a.y, w: w * pv.scale, h: h * pv.scale })) handle(hd.x, hd.y);
  };
  if (sel.kind === 'box') { const b = c.boxes.find((x) => x.id === sel.id); if (b) rect(b.x, b.y, b.w, b.h); }
  else if (sel.kind === 'image') { const im = c.images.find((x) => x.id === sel.id); if (im) rect(im.x, im.y, im.w, im.h); }
  else if (sel.kind === 'line') {
    const ln = c.lines.find((x) => x.id === sel.id);
    if (ln) {
      // Lone selection: grab handles at each vertex. Multi-selection: stroke the
      // polyline itself as the highlight (no per-vertex handles to declutter).
      if (withHandles) for (const p of ln.points) { const s = paperToScreen(pv, p.x, p.y); handle(s.x, s.y); }
      else { ctx.beginPath(); ln.points.forEach((p, i) => { const s = paperToScreen(pv, p.x, p.y); i ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); }); ctx.stroke(); }
    }
  } else if (sel.kind === 'conn') {
    // The conn itself is recoloured by drawConns; add waypoint handles for a lone selection.
    if (withHandles) {
      const cn = c.conns.find((x) => x.id === sel.id);
      const route = cn ? schConnRoute(sh, cn) : null;
      if (route) for (const p of cn!.waypoints ?? []) { const s = paperToScreen(pv, p.x, p.y); handle(s.x, s.y); }
    }
  }
  ctx.restore();
}
